// @flow
import * as React from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import {DivRight, LayoutInline, LayoutWrapper, useLayoutState} from 'components/Layout';
import {LocalizedParagraph, StyledDiv, useLocalized, type LocalizedText} from 'components/Localized';
import {useSessionState, useSessionDispatch, getExteriorColorIndex} from 'components/Session';
import ButtonNextPage from 'components/ButtonNextPage';
import CheckIconW from 'assets/images/driving-zone/img-color-check-w.svg';
import CheckIconB from 'assets/images/driving-zone/img-color-check-b.svg';
import * as VRUtils from 'utils/vrUtils';

type Props = {
  open: boolean,
  onClose: () => void,
};

const color_tags = [
    "Aurora black pearl",
    "Snow white pearl",
    "Runway red",
    "Moonscape (Matte)",
    "Yacht blue",
  ];

const color_tags_ga = [
    "Aurora Black Pear",
    "Snow White Pearl",
    "Runway Red",
    "Moonscape",
    "Yacht Blue",
  ];

export default function ModalExteriorColor(props: Props): React.Element<any> {
  const layoutState = useLayoutState();
  const localized = useLocalized();
  const sessionState = useSessionState();
  const sessionDispatch = useSessionDispatch();
  const color = getExteriorColorIndex(sessionState.selectedColorIndices, sessionState.carIndex);
  const open = props.open;
  //const modalExteriorColorRef = React.useRef(null);
  const colors = React.useMemo<Array<LocalizedText>>(() => {
    return [
      localized.driving_a01_color_001,
      localized.driving_a01_color_002,
      localized.driving_a01_color_003,
      localized.driving_a01_color_004,
      localized.driving_a01_color_005,
    ];
  }, [localized]);


  const backColors = React.useMemo(() => {
    return [
      ['#161616', '#000000'],
      ['#f6f6f6', '#cacaca'],
      ['#ec0000', '#c10000'],
      ['#b3b3b3', '#939393'],
      ['#00008b', '#000072'],
    ];
  }, []);

  const handleSelect = React.useCallback((index: number) => {
    window.tagManager.colorSelctionLinkClick((window.useGA?color_tags_ga:color_tags)[index], sessionState.carIndex === 1 );
    sessionDispatch({type: 'SELECT_EXTERIOR_COLOR', data: index});
  }, [sessionDispatch, sessionState.carIndex]);

  const handleOK = React.useCallback(() => {
    window.tagManager.colorSelctionOKLinkClick((window.useGA?color_tags_ga:color_tags)[color]);
    props.onClose();
  }, [props, color]);

  React.useEffect(() => {
    sessionDispatch({type: 'APPLY_EXTERIOR_COLOR', data: null});
  },[props.open, sessionDispatch])

  

  const refModalExteriorColor = React.useCallback(e => {

    VRUtils.ready(
      ()=>{

        if (open && e != null) {
          window.pc.app.graphicsDevice.canvas.style.left = - Math.floor(parseInt(e.offsetWidth) / 2) + "px";
        } else {
          window.pc.app.graphicsDevice.canvas.style.left = "0px";
        }
      }
      );

  }, [open]);


  return (
    <StyledModal 
      isOpen={props.open}
      contentLabel="Exterior Color Modal"
      overlayClassName="modal-overlay"

      >
      <Back datascale={layoutState.scale} />
      <ModalWrapper ref = {refModalExteriorColor}>
        <DivTitle><LocalizedParagraph {...localized.driving_a01_color_007} /></DivTitle>
        <DivGrid>
        {colors.map((value, index) => (
          <ButtonColor key={index} onClick={() => handleSelect(index) }>
            <ButtonColorBackground datacolors={backColors[index]} />
            <ButtonColorText {...value} show={index === color}>
              <img src={value.datacolor === '#ffffff' ? CheckIconW : CheckIconB} alt="" />
              <span>{value.datatext}</span>
            </ButtonColorText>
          </ButtonColor>
        ))}
        </DivGrid>
      </ModalWrapper>
      <ButtonNextPage
        title={localized.driving_a01_color_006}
        onNext={handleOK}
        dataBackground="#05141f"
        marginRightM={10}
      />
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0px;
  top: 0px;
  pointer-events: none;
`;

const Back = styled(LayoutWrapper)`
  width: calc((${props => props.theme.layout.width}px - 100%) * 0.5 + ${props => props.theme.layout.mobile ? (235 * props.datascale) : (430 * props.datascale)}px);
  background-color: #ffffff;
  position: absolute;
  left: auto;
`;

const DivTitle = styled.div`
  padding-top: ${props => props.theme.layout.mobile ? 31 * props.theme.layout.scale : 97 * props.theme.layout.scale}px;
  padding-bottom: ${props => props.theme.layout.mobile ? 22 * props.theme.layout.scale : 25 * props.theme.layout.scale}px;
  text-align: center;
`;

const ModalWrapper = styled(DivRight)`
  width: ${props => props.theme.layout.mobile ? (235 * props.theme.layout.scale) : (430 * props.theme.layout.scale)}px;
  background-color: #ffffff;
`;

const DivGrid = styled(LayoutInline)`
  width: calc(100% - ${props => props.theme.layout.mobile ? 10 : 160}px);
  padding: 0px ${props => props.theme.layout.mobile ? 10 : 80}px 0px;
  display: grid;
  grid-template-columns: repeat(2, ${props => props.theme.layout.mobile ? 105 : 130}px);
  grid-gap: ${props => props.theme.layout.mobile ? 5 : 10}px;
  grid-auto-rows: ${props => props.theme.layout.mobile ? 75 : 130}px;
  transform-origin: left top;
`;

const ButtonColor = styled.button`
  width: 100%;
  height: 100%;
  position: relative;
`;

const ButtonColorBackground = styled.div`
  border-color: ${(props) => props.datacolors?.[0]} ${(props) => props.datacolors?.[1]} transparent
    transparent;
  border-style: solid;
  border-width: ${props => props.theme.layout.mobile ? 75 : 130}px ${props => props.theme.layout.mobile ? 105 : 130}px 0px 0px;
  height: 0px;
  width: 0px;
  position: absolute;
  left: 0px;
  top: 0px;
`;

const ButtonColorText = styled(StyledDiv)`
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  position: absolute;
  margin: 10px 10px;
  left: 0px;
  top: 0px;
  visibility: ${(props) => (props.show ? 'visible' : 'hidden')};
  text-align: left;
  text-align: start;

  img {
    display: block;
    width: ${props => props.theme.layout.mobile ? 15 : 20}px;
    height: ${props => props.theme.layout.mobile ? 11 : 14}px;
    padding-bottom: ${props => props.theme.layout.mobile ? 5 : 10}px;
  }

  span {
    display: block;
  }
`;
